import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { useField } from 'react-final-form';
import { CheckBoxField, DateField, FlexColumn, FormModal } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import { required } from '@ubeya/shared/utils/form';
import moment from 'moment';
import { formatBalanceLine } from '../leaveBalanceUtils';

const Container = styled(FlexColumn)`
  gap: 16px;
  padding: 16px 28px;
`;

const WarningBanner = styled.div`
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.yellowLight};
  border: 1px solid ${({ theme }) => theme.colors.yellowPartially};
  border-radius: 4px;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.black};
`;

const WarningTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const formatDate = (value) => (value ? moment(value).format(API_DATE_FORMAT) : undefined);

// Future endDate would stamp payout / forfeit events with occurredAt > today;
// year-end transitions and balance reads break when the ledger contains future-dated rows.
const isFutureDay = (day) => day && moment(day).startOf('day').isAfter(moment().startOf('day'));

const UnsettledBalanceWarning = ({ management }) => {
  const { t } = useContext(ConfigContext);
  const { input } = useField('payoutRemainder', { subscription: { value: true } });

  const positiveBalances = useMemo(
    () => (management?.balances || []).filter((balance) => Number(balance.balance) > 0),
    [management]
  );

  if (input.value || positiveBalances.length === 0) return null;

  return (
    <WarningBanner>
      <WarningTitle>{t('terminate_unsettledBalance_title')}</WarningTitle>
      <div>
        {t('terminate_unsettledBalance_body', {
          balances: positiveBalances.map((balance) => formatBalanceLine(balance, t)).join(', ')
        })}
      </div>
    </WarningBanner>
  );
};

const TerminateAssignmentModal = ({ onClose, terminateAssignment, management, defaultPayoutRemainder = false }) => {
  const { t } = useContext(ConfigContext);

  const initialValues = useMemo(
    () => ({
      endDate: moment().format(API_DATE_FORMAT),
      payoutRemainder: defaultPayoutRemainder
    }),
    [defaultPayoutRemainder]
  );

  const onSubmit = useCallback(
    async (values) => {
      try {
        await terminateAssignment({
          assignmentId: management.employeeContractV2Id,
          endDate: formatDate(values.endDate),
          payoutRemainder: !!values.payoutRemainder
        });
        onClose();
      } catch {
        // useOptimisticMutation already shows the toaster.
      }
    },
    [terminateAssignment, management, onClose]
  );

  return (
    <FormModal
      onSubmit={onSubmit}
      initialValues={initialValues}
      size="small"
      onClose={onClose}
      title={t('terminateLeaveAssignment')}
      confirmText={t('terminate')}
      showCancelButton
      disabledOnPristine={false}>
      <Container>
        <p>{t('terminateLeaveAssignmentDescription')}</p>
        <DateField
          name="endDate"
          title={t('endDate')}
          validate={required}
          required
          usePortal
          isOutsideRange={isFutureDay}
        />
        <CheckBoxField name="payoutRemainder" title={t('payoutRemainder')} />
        <UnsettledBalanceWarning management={management} />
      </Container>
    </FormModal>
  );
};

export default TerminateAssignmentModal;
